import { Box } from "@modernatx-internal/component-library";
import { usePathname } from "next/navigation";
import React from "react";

import { PageHeaderLink } from "./PageHeaderLink";

interface PageHeaderSubnavState {
  links: React.ReactNode;
  target: HTMLElement | null;
}

interface PageHeaderSubnavContextValue extends PageHeaderSubnavState {
  setPageHeaderSubnav: (state: PageHeaderSubnavState) => void;
}

const PageHeaderSubnavContext = React.createContext<PageHeaderSubnavContextValue>({
  links: null,
  setPageHeaderSubnav: () => {},
  target: null
});

export const PageHeaderSubnavProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [{ links, target }, stateSet] = React.useState<PageHeaderSubnavState>({
    links: null,
    target: null
  });

  const setPageHeaderSubnav = React.useCallback((state: PageHeaderSubnavState) => {
    stateSet(state);
  }, []);

  return (
    <PageHeaderSubnavContext.Provider
      value={{
        links,
        setPageHeaderSubnav,
        target
      }}
    >
      {children}
    </PageHeaderSubnavContext.Provider>
  );
};

export const usePageHeaderSubnav = () => React.useContext(PageHeaderSubnavContext);

export const PageHeaderSubnavDesktop: React.FC = () => {
  const { links, target } = usePageHeaderSubnav();
  const [position, positionSet] = React.useState<{
    left: string | number;
    top: string | number;
    width: string | number;
  }>({ left: 0, top: 0, width: 0 });
  const [, setForceUpdate] = React.useState(0);
  const visible = React.useRef(false);
  const visibleAndPositioned = position?.left && position?.top;

  const getPosition = React.useCallback(() => {
    if (target) {
      const rect = target.getBoundingClientRect();
      const rectChild = target.children[0]?.getBoundingClientRect();
      const rectParent = target.offsetParent?.getBoundingClientRect();
      const bottom = rect.bottom - (rectParent?.top || 0);
      const horizontalCenter = rect.left + rect.width / 2;

      return {
        left: `${horizontalCenter}px`,
        top: `${bottom}px`,
        width: rectChild ? `${rectChild.width}px` : 0
      };
    }

    return {
      left: 0,
      top: 0,
      width: 0
    };
  }, [target]);

  React.useEffect(() => {
    // Force a render on mount so the element exists and
    // transitions start working
    setForceUpdate(1);
  }, []);

  React.useEffect(() => {
    const handleSetPosition = () => {
      positionSet(getPosition());
    };

    window.addEventListener("resize", handleSetPosition);
    window.addEventListener("scroll", handleSetPosition);

    return () => {
      window.removeEventListener("resize", handleSetPosition);
      window.removeEventListener("scroll", handleSetPosition);
    };
  }, [getPosition]);

  React.useEffect(() => {
    positionSet(getPosition());
  }, [getPosition]);

  React.useEffect(() => {
    if (links) {
      setTimeout(() => {
        visible.current = true;
      });
    }
  }, [links]);

  if (!links) {
    visible.current = false;
  }

  return (
    <Box
      sx={{
        borderColor: "tab-stroke-selected",
        borderEndEndRadius: "radius-1",
        borderEndStartRadius: "radius-1",
        borderTopStyle: "solid",
        borderTopWidth: () => "3px",
        boxShadow: "shadow-400",
        display: "flex",
        flexDirection: "column",
        height: visibleAndPositioned ? null : 0,
        left: () => position?.left || 0,
        minWidth: () => position?.width || 0,
        opacity: visibleAndPositioned ? 1 : 0,
        overflow: "hidden",
        position: "absolute",
        top: () => position?.top || 0,
        transform: visibleAndPositioned
          ? `translate3d(-50%, -3px, 0)`
          : `translate3d(-50%, 3px, 0)`,
        transition: () => "opacity 100ms, transform 100ms ease-out",
        visibility: ["hidden", null, visibleAndPositioned ? "visible" : "hidden"],
        zIndex: "dropdown"
      }}
    >
      {links}
    </Box>
  );
};

type PageHeaderLinkProps = React.ComponentProps<typeof PageHeaderLink>;
type PageHeaderLinkElement = React.ReactElement<PageHeaderLinkProps, typeof PageHeaderLink>;

export const PageHeaderSubnavMobile: React.FC<{ links: React.ReactNode }> = ({ links }) => {
  const pathname = usePathname();
  const activeLink = React.useMemo<PageHeaderLinkElement | null>(() => {
    let activeLinkResult: PageHeaderLinkElement | null = null;

    React.Children.forEach(links, (link) => {
      if (activeLinkResult) {
        // Do nothing
      } else if (React.isValidElement(link)) {
        const pageHeaderLink = link as PageHeaderLinkElement;
        const { href, links: pageHeaderLinkLinks } = pageHeaderLink.props;

        if (href && pathname.includes(href)) {
          activeLinkResult = pageHeaderLink;
        }

        if (!activeLinkResult) {
          React.Children.forEach(pageHeaderLinkLinks, (pageHeaderLinkLink) => {
            if (
              React.isValidElement(pageHeaderLinkLink) &&
              pathname.includes(pageHeaderLinkLink.props.href)
            ) {
              activeLinkResult = pageHeaderLink;
            }
          });
        }
      }
    });

    return activeLinkResult;
  }, [links, pathname]);
  const activeLinks = activeLink ? activeLink.props.links : null;

  if (!activeLinks) {
    return null;
  }

  return (
    <Box
      // eslint-disable-next-line jsx-a11y/aria-role
      role="header"
      sx={{
        backgroundColor: "surface-neutral",
        display: ["block", null, "none"]
      }}
    >
      <Box
        sx={{
          borderColor: "stroke-neutral-secondary",
          borderStyle: "solid",
          borderWidth: "0 0 1px 0",
          display: "flex",
          flexDirection: "column",
          px: [5, null, 8]
        }}
      >
        {activeLinks}
      </Box>
    </Box>
  );
};
